import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getQuotes } from "../services/quoteService";
import { formatCurrency } from "../utils/currency";
import QuoteDetails from "./QuoteDetails";
import QuoteDetailsModal from "../components/QuoteDetailsModal";

import {
  FileText,
  Image as ImageIcon,
  Sparkles,
  AlertTriangle,
  Search,
  Filter,
  Pencil,
  User,
} from "lucide-react";

function formatDateTime(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("en-DK", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function formatPercent(value) {
  return `${Math.round(Number(value || 0) * 100)}%`;
}

function getOverallVisionConfidence(meta) {
  const overall = meta?.visionConfidence?.overall;

  if (typeof overall === "number" && Number.isFinite(overall)) {
    return overall;
  }

  const values = Object.values(meta?.visionConfidence || {}).filter(
    (value) => typeof value === "number" && Number.isFinite(value)
  );

  if (!values.length) return null;

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getConfidenceLevel(meta) {
  const score = getOverallVisionConfidence(meta);

  if (score === null) return "unknown";
  if (score >= 0.8) return "high";
  if (score >= 0.5) return "medium";
  return "low";
}

function getConfidenceConfig(level) {
  switch (level) {
    case "high":
      return { label: "High confidence", tone: "green" };
    case "medium":
      return { label: "Medium confidence", tone: "yellow" };
    case "low":
      return { label: "Low confidence", tone: "red" };
    default:
      return { label: "Confidence unavailable", tone: "default" };
  }
}

function StatusBadge({ children, tone = "default" }) {
  const toneClasses = {
    default: "bg-[#f3f2f1] text-[#605e5c] border-[#edebe9]",
    blue: "bg-[#deecf9] text-[#004578] border-[#c7e0f4]",
    yellow: "bg-[#fff4ce] text-[#8a6d1d] border-[#f3d6a0]",
    red: "bg-[#fde7e9] text-[#a4262c] border-[#f1aeb5]",
    green: "bg-[#dff6dd] text-[#107c10] border-[#c7e0c0]",
  };

  return (
    <span
      className={[
        "inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium",
        toneClasses[tone] || toneClasses.default,
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function MetricCard({ label, value, icon: Icon, highlight = false }) {
  return (
    <div
      className={[
        "rounded-sm border px-3 py-2 shadow-sm",
        highlight
          ? "border-[#c7e0c0] bg-[#dff6dd] text-[#107c10]"
          : "border-[#edebe9] bg-white text-[#201f1e]",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        {Icon && (
          <Icon
            size={14}
            className={highlight ? "text-[#107c10]" : "text-[#605e5c]"}
          />
        )}

        <div
          className={[
            "text-[11px] font-semibold uppercase tracking-wide",
            highlight ? "text-[#107c10]" : "text-[#605e5c]",
          ].join(" ")}
        >
          {label}
        </div>
      </div>

      <div className="mt-1 text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, right }) {
  return (
    <section className="overflow-hidden rounded-sm border border-[#edebe9] bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-[#edebe9] bg-[#faf9f8] px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-[#323130]">
          {Icon && <Icon size={16} className="text-[#605e5c]" />}
          <h2 className="text-sm font-semibold text-[#201f1e]">{title}</h2>
        </div>

        {right}
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function getStatusTone(status) {
  if (status === "approved") return "green";
  if (status === "sent") return "blue";
  return "default";
}

function getQuoteCurrency(quote) {
  const parsed = quote?.parsed_json || {};
  const estimate = quote?.estimate_json || {};
  const meta = estimate.meta || {};

  if (parsed.currency === "SEK" || parsed.currency === "DKK") return parsed.currency;
  if (meta.currency === "SEK" || meta.currency === "DKK") return meta.currency;

  return parsed.country === "SE" ? "SEK" : "DKK";
}

function getQuoteLanguage(quote) {
  const parsed = quote?.parsed_json || {};
  const estimate = quote?.estimate_json || {};
  const meta = estimate.meta || {};

  if (parsed.quoteLanguage) return parsed.quoteLanguage;
  if (meta.quoteLanguage) return meta.quoteLanguage;
  return parsed.country === "SE" ? "sv" : "da";
}

function getCustomerFromQuote(quote) {
  const relatedCustomer = quote?.customers;
  const snapshot =
    quote?.customer_snapshot ||
    quote?.parsed_json?.customer ||
    quote?.parsed_json?.customerSnapshot;

  return relatedCustomer || snapshot || null;
}

function getCustomerName(quote) {
  const customer = getCustomerFromQuote(quote);

  return (
    customer?.name ||
    customer?.full_name ||
    customer?.company_name ||
    customer?.email ||
    ""
  );
}

function getCustomerEmail(quote) {
  const customer = getCustomerFromQuote(quote);
  return customer?.email || "";
}

function getAiAdjustedCount(rows = []) {
  return rows.filter((x) => x?.aiAdjusted || x?.flags?.aiAdjusted).length;
}

export default function QuotesList() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reviewModeOnly, setReviewModeOnly] = useState(false);

  const navigate = useNavigate();
const { id } = useParams();
const location = useLocation();

  useEffect(() => {
  loadQuotes();
}, [location.key]);

async function loadQuotes() {
  setLoading(true);

  try {
    const data = await getQuotes();
    setQuotes(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to load quotes:", error);
    setQuotes([]);
  } finally {
    setLoading(false);
  }
}

  const filteredQuotes = useMemo(() => {
    const q = search.trim().toLowerCase();

    return quotes.filter((quote) => {
      const estimate = quote.estimate_json || {};
      const meta = estimate.meta || {};
      const confidenceLevel = getConfidenceLevel(meta);
      const reviewFlags = meta.reviewFlags || [];
      const photoScopeNotes = meta.photoScopeNotes || [];
      const quoteCurrency = getQuoteCurrency(quote);
      const quoteLanguage = getQuoteLanguage(quote);
      const customerName = getCustomerName(quote);
      const customerEmail = getCustomerEmail(quote);

      const totalAiAdjustedRows =
        getAiAdjustedCount(estimate.materials) +
        getAiAdjustedCount(estimate.labour) +
        getAiAdjustedCount(estimate.extras);

      const matchesReviewMode =
        !reviewModeOnly ||
        confidenceLevel === "low" ||
        reviewFlags.length > 0 ||
        photoScopeNotes.length > 0 ||
        totalAiAdjustedRows > 0;

      const requestText = String(quote.request_text || "").toLowerCase();
      const status = String(quote.status || "draft").toLowerCase();

      const matchesSearch =
        !q ||
        requestText.includes(q) ||
        status.includes(q) ||
        String(quote.id || "").toLowerCase().includes(q) ||
        String(quoteCurrency).toLowerCase().includes(q) ||
        String(quoteLanguage).toLowerCase().includes(q) ||
        String(customerName).toLowerCase().includes(q) ||
        String(customerEmail).toLowerCase().includes(q);

      return matchesReviewMode && matchesSearch;
    });
  }, [quotes, reviewModeOnly, search]);

  const summaryStats = useMemo(() => {
    const total = filteredQuotes.length;

    const lowConfidence = filteredQuotes.filter((quote) => {
      const meta = quote.estimate_json?.meta || {};
      return getConfidenceLevel(meta) === "low";
    }).length;

    const needsReview = filteredQuotes.filter((quote) => {
      const estimate = quote.estimate_json || {};
      const meta = estimate.meta || {};
      const reviewFlags = meta.reviewFlags || [];
      const photoScopeNotes = meta.photoScopeNotes || [];

      return (
        reviewFlags.length > 0 ||
        photoScopeNotes.length > 0 ||
        getAiAdjustedCount(estimate.materials) +
          getAiAdjustedCount(estimate.labour) +
          getAiAdjustedCount(estimate.extras) >
          0
      );
    }).length;

    const linkedCustomers = filteredQuotes.filter((quote) =>
      Boolean(getCustomerName(quote))
    ).length;

    const totalValueByCurrency = filteredQuotes.reduce((acc, quote) => {
      const currency = getQuoteCurrency(quote);
      const totalAmount = Number(
  quote.estimate_json?.summary?.total ??
    quote.estimate_json?.summary?.totalInclVat ??
    quote.estimate_json?.summary?.grandTotal ??
    0
);

      acc[currency] = (acc[currency] || 0) + totalAmount;
      return acc;
    }, {});

    return {
      total,
      lowConfidence,
      needsReview,
      linkedCustomers,
      totalValueByCurrency,
    };
  }, [filteredQuotes]);

  const totalValueChips = useMemo(() => {
    const entries = Object.entries(summaryStats.totalValueByCurrency || {}).filter(
      ([, value]) => Number(value) > 0
    );

    return entries.map(([currency, value]) => ({
      currency,
      formatted: formatCurrency(value, currency),
    }));
  }, [summaryStats]);

  return (
    <>
      <div
        className={[
          "min-h-screen bg-[#f3f2f1] px-6 py-6 text-[#201f1e]",
          id ? "pointer-events-none select-none blur-[2px]" : "",
        ].join(" ")}
      >
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-[#201f1e]">
                Saved Quotes
              </h1>
              <p className="mt-1 text-sm text-[#605e5c]">
                Review saved quotes, customers, confidence level, and AI signals.
              </p>
            </div>
          </div>

          <SectionCard
            title="Overview"
            icon={Sparkles}
            right={
              <button
                type="button"
                onClick={() => setReviewModeOnly((prev) => !prev)}
                className={[
                  "inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-sm transition",
                  reviewModeOnly
                    ? "border-[#f3d6a0] bg-[#fff4ce] text-[#8a6d1d]"
                    : "border-[#edebe9] bg-white text-[#323130] hover:bg-[#f3f2f1]",
                ].join(" ")}
              >
                <Filter size={16} />
                {reviewModeOnly ? "Review Mode On" : "Review Mode Off"}
              </button>
            }
          >
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <MetricCard label="Quotes" value={String(summaryStats.total)} icon={FileText} />
              <MetricCard
                label="Customers"
                value={String(summaryStats.linkedCustomers)}
                icon={User}
              />
              <MetricCard
                label="Needs Review"
                value={String(summaryStats.needsReview)}
                icon={AlertTriangle}
              />
              <MetricCard
                label="Low Confidence"
                value={String(summaryStats.lowConfidence)}
                icon={Sparkles}
              />
              <MetricCard
                label="Visible Totals"
                value={
                  totalValueChips.length
                    ? totalValueChips.map((item) => item.formatted).join(" • ")
                    : "—"
                }
                icon={Sparkles}
                highlight
              />
            </div>

            <div className="mt-4">
              <label className="relative block">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#605e5c]"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search request, customer, status, currency, language or quote ID..."
                  className="w-full rounded-sm border border-[#edebe9] bg-white py-3 pl-10 pr-4 text-sm text-[#201f1e] outline-none placeholder:text-[#605e5c] focus:border-[#0f6cbd] focus:ring-1 focus:ring-[#0f6cbd]"
                />
              </label>
            </div>

            {totalValueChips.length > 1 && (
              <div className="mt-4 rounded-sm border border-[#c7e0f4] bg-[#deecf9] px-4 py-3 text-sm text-[#004578]">
                Totals are shown per currency to avoid mixing DKK and SEK into
                one number.
              </div>
            )}

            {reviewModeOnly && (
              <div className="mt-4 rounded-sm border border-[#f3d6a0] bg-[#fff4ce] px-4 py-3 text-sm text-[#8a6d1d]">
                Review mode is active. Showing quotes with low confidence,
                review flags, photo notes, or AI-adjusted rows.
              </div>
            )}
          </SectionCard>

          <SectionCard title="Quotes" icon={FileText}>
            {loading ? (
              <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-6 text-sm text-[#605e5c]">
                Loading quotes...
              </div>
            ) : filteredQuotes.length === 0 ? (
              <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-6 text-sm text-[#605e5c]">
                No quotes found.
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredQuotes.map((q) => {
                  const estimate = q.estimate_json || {};
                  const meta = estimate.meta || {};
                  const confidenceScore = getOverallVisionConfidence(meta);
                  const confidenceLevel = getConfidenceLevel(meta);
                  const confidenceConfig = getConfidenceConfig(confidenceLevel);
                  const reviewFlags = meta.reviewFlags || [];
                  const photoScopeNotes = meta.photoScopeNotes || [];
                  const quoteCurrency = getQuoteCurrency(q);
                  const quoteLanguage = getQuoteLanguage(q);
                  const customerName = getCustomerName(q);
                  const customerEmail = getCustomerEmail(q);

                  const totalAiAdjustedRows =
                    getAiAdjustedCount(estimate.materials) +
                    getAiAdjustedCount(estimate.labour) +
                    getAiAdjustedCount(estimate.extras);

                  const total = estimate.summary?.total || 0;
                  const imageCount = q.image_urls?.length || 0;

                  const requestPreview = q.request_text
                    ? q.request_text.length > 220
                      ? `${q.request_text.slice(0, 220)}...`
                      : q.request_text
                    : "No request text";

                  return (
                    <div
                      key={q.id}
                      className="w-full rounded-sm border border-[#edebe9] bg-white p-5 shadow-sm transition hover:border-[#c8c6c4] hover:bg-[#faf9f8]"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <button
                          type="button"
                          onClick={() => navigate(`/quotes-list/${q.id}`)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge tone={getStatusTone(q.status)}>
                              {q.status
                                ? q.status.charAt(0).toUpperCase() + q.status.slice(1)
                                : "Draft"}
                            </StatusBadge>

                            {customerName && (
                              <StatusBadge tone="green">{customerName}</StatusBadge>
                            )}

                            <StatusBadge tone={confidenceConfig.tone}>
                              {confidenceConfig.label}
                            </StatusBadge>

                            {confidenceLevel === "low" && (
                              <StatusBadge tone="red">Needs review</StatusBadge>
                            )}

                            {totalAiAdjustedRows > 0 && (
                              <StatusBadge tone="yellow">
                                {totalAiAdjustedRows} AI adjusted
                              </StatusBadge>
                            )}

                            <StatusBadge tone="default">{quoteCurrency}</StatusBadge>

                            <StatusBadge tone="default">
                              {quoteLanguage === "da"
                                ? "Danish"
                                : quoteLanguage === "sv"
                                ? "Swedish"
                                : "English"}
                            </StatusBadge>
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#605e5c]">
                            <span>{formatDateTime(q.created_at)}</span>

                            {customerName && (
                              <span className="inline-flex items-center gap-1 text-[#323130]">
                                <User size={14} className="text-[#605e5c]" />
                                {customerName}
                              </span>
                            )}

                            {customerEmail && (
                              <span className="text-[#605e5c]">{customerEmail}</span>
                            )}
                          </div>

                          <div className="mt-3 text-sm leading-6 text-[#323130]">
                            {requestPreview}
                          </div>
                        </button>

                        <div className="min-w-[280px] space-y-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <MetricCard
                              label="Total"
                              value={formatCurrency(total, quoteCurrency)}
                              icon={Sparkles}
                              highlight
                            />
                            <MetricCard
                              label="Images"
                              value={String(imageCount)}
                              icon={ImageIcon}
                            />
                            <MetricCard
                              label="Review Flags"
                              value={String(reviewFlags.length)}
                              icon={AlertTriangle}
                            />
                            <MetricCard
                              label="Vision Confidence"
                              value={
                                confidenceScore === null
                                  ? "Not available"
                                  : formatPercent(confidenceScore)
                              }
                              icon={Sparkles}
                            />
                          </div>

                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => navigate(`/quote-editor/${q.id}`)}
                              className="inline-flex items-center gap-2 rounded-sm border border-[#0f6cbd] bg-[#0f6cbd] px-3 py-2 text-sm font-medium text-white hover:bg-[#115ea3]"
                            >
                              <Pencil size={16} />
                              Edit Quote
                            </button>
                          </div>
                        </div>
                      </div>

                      {(reviewFlags.length > 0 || photoScopeNotes.length > 0) && (
                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                          <div className="rounded-sm border border-[#edebe9] bg-[#faf9f8] p-4">
                            <div className="mb-2 text-sm font-medium text-[#201f1e]">
                              Backend review flags
                            </div>

                            {reviewFlags.length ? (
                              <ul className="space-y-2 pl-4 text-sm text-[#8a6d1d]">
                                {reviewFlags.slice(0, 3).map((item, index) => (
                                  <li key={index} className="list-disc">
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-sm text-[#605e5c]">
                                No backend review flags.
                              </div>
                            )}
                          </div>

                          <div className="rounded-sm border border-[#edebe9] bg-[#faf9f8] p-4">
                            <div className="mb-2 text-sm font-medium text-[#201f1e]">
                              Photo scope notes
                            </div>

                            {photoScopeNotes.length ? (
                              <ul className="space-y-2 pl-4 text-sm text-[#605e5c]">
                                {photoScopeNotes.slice(0, 3).map((item, index) => (
                                  <li key={index} className="list-disc">
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-sm text-[#605e5c]">
                                No photo scope notes.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {id && (
        <QuoteDetailsModal onClose={() => navigate("/quotes-list")}>
          <QuoteDetails quoteId={id} />
        </QuoteDetailsModal>
      )}
    </>
  );
}